import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import {
  ArrowLeft, DollarSign, Edit, Save, TrendingUp, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const RateConfigManager = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      const res = await api.get('/rates/all-configs');
      setConfigs(res.data);
    } catch (error) {
      toast.error('Failed to load rate configs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setEditingRole(config);
    setForm({
      min_bill_rate: config.min_bill_rate,
      recommended_bill_rate_low: config.recommended_bill_rate_low,
      recommended_bill_rate_high: config.recommended_bill_rate_high,
      premium_bill_rate: config.premium_bill_rate || '',
      min_talent_payout: config.min_talent_payout,
      max_talent_payout: config.max_talent_payout,
      entry_payout: config.entry_payout || '',
      mid_payout: config.mid_payout || '',
      premium_payout: config.premium_payout || '',
      platform_margin_pct: config.platform_margin_pct,
      admin_override_allowed: true
    });
  };

  const handleSave = async () => {
    try {
      await api.put(`/rates/admin/config/${editingRole.role_id}`, {
        ...form,
        min_bill_rate: parseFloat(form.min_bill_rate),
        recommended_bill_rate_low: parseFloat(form.recommended_bill_rate_low),
        recommended_bill_rate_high: parseFloat(form.recommended_bill_rate_high),
        premium_bill_rate: form.premium_bill_rate ? parseFloat(form.premium_bill_rate) : null,
        min_talent_payout: parseFloat(form.min_talent_payout),
        max_talent_payout: parseFloat(form.max_talent_payout),
        entry_payout: form.entry_payout ? parseFloat(form.entry_payout) : null,
        mid_payout: form.mid_payout ? parseFloat(form.mid_payout) : null,
        premium_payout: form.premium_payout ? parseFloat(form.premium_payout) : null,
        platform_margin_pct: parseFloat(form.platform_margin_pct)
      });
      toast.success(`Rate config updated for ${editingRole.role_name}`);
      setEditingRole(null);
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const getTierBadge = (tier) => {
    const map = {
      labor: 'bg-gray-100 text-gray-700',
      hospitality: 'bg-blue-100 text-blue-700',
      premium_activation: 'bg-amber-100 text-amber-700'
    };
    return map[tier] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="rate-config-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-6 h-6 text-cyan-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rate Configuration</h1>
            <p className="text-gray-500 text-sm">Manage bill rates, payout ranges, and platform margins per role</p>
          </div>
        </div>

        {/* Config Cards */}
        <div className="grid gap-4">
          {configs.map((c) => (
            <Card key={c.role_id} className="bg-white border-gray-200" data-testid="rate-config-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="font-semibold text-gray-900">{c.role_name}</h3>
                      <Badge className={getTierBadge(c.tier_type)}>{c.tier_type?.replace('_', ' ')}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Bill Rate Range</p>
                        <p className="text-sm font-medium text-gray-900">
                          ${c.recommended_bill_rate_low} - ${c.recommended_bill_rate_high}
                          <span className="text-xs text-gray-400 ml-1">/hr</span>
                        </p>
                        <p className="text-[10px] text-gray-400">Min: ${c.min_bill_rate}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Payout Range</p>
                        <p className="text-sm font-medium text-gray-900">
                          ${c.min_talent_payout} - ${c.max_talent_payout}
                          <span className="text-xs text-gray-400 ml-1">/hr</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Payout Tiers</p>
                        <p className="text-xs text-gray-600">
                          Entry: ${c.entry_payout} | Mid: ${c.mid_payout} | Premium: ${c.premium_payout}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-0.5">Platform Margin</p>
                        <p className="text-sm font-bold text-cyan-600">{c.platform_margin_pct}%</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleEdit(c)}
                    data-testid="edit-rate-btn"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingRole} onOpenChange={() => setEditingRole(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Rate Config: {editingRole?.role_name}</DialogTitle>
          <DialogDescription className="sr-only">Edit Rate Config:</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Min Bill Rate</Label>
                <Input type="number" step="0.01" value={form.min_bill_rate} onChange={(e) => setForm({...form, min_bill_rate: e.target.value})} data-testid="min-bill-rate-input" />
              </div>
              <div>
                <Label className="text-xs">Recommended Low</Label>
                <Input type="number" step="0.01" value={form.recommended_bill_rate_low} onChange={(e) => setForm({...form, recommended_bill_rate_low: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Recommended High</Label>
                <Input type="number" step="0.01" value={form.recommended_bill_rate_high} onChange={(e) => setForm({...form, recommended_bill_rate_high: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">Premium Bill Rate</Label>
                <Input type="number" step="0.01" value={form.premium_bill_rate} onChange={(e) => setForm({...form, premium_bill_rate: e.target.value})} />
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Talent Payout Tiers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Min Payout</Label>
                  <Input type="number" step="0.01" value={form.min_talent_payout} onChange={(e) => setForm({...form, min_talent_payout: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Max Payout</Label>
                  <Input type="number" step="0.01" value={form.max_talent_payout} onChange={(e) => setForm({...form, max_talent_payout: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Entry Level</Label>
                  <Input type="number" step="0.01" value={form.entry_payout} onChange={(e) => setForm({...form, entry_payout: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Mid Level</Label>
                  <Input type="number" step="0.01" value={form.mid_payout} onChange={(e) => setForm({...form, mid_payout: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Premium Level</Label>
                  <Input type="number" step="0.01" value={form.premium_payout} onChange={(e) => setForm({...form, premium_payout: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Platform Margin %</Label>
                  <Input type="number" step="0.01" value={form.platform_margin_pct} onChange={(e) => setForm({...form, platform_margin_pct: e.target.value})} data-testid="margin-input" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingRole(null)}>Cancel</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white" onClick={handleSave} data-testid="save-rate-btn">
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RateConfigManager;
